import React, { useEffect, useState } from "react"
import { KInput, KSpan, KTitleSpan, KDropdown, KButton, KLogo, KTooltip } from "kahuna-base-react-components"
import TableNew from "../../ui/table/TableNew"
import { getSelectedLanguage, lang } from "../../../constants/languages"
import playlistsColumns from "./playlistsColumns"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { PaginatedData, TrackPlaylist } from "../../../types"
import { fetchTrackPlaylists } from "../../../actions/insightsActions"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { useNavigate } from "react-router-dom"
//@ts-ignore
import SpotifyLogo from "../../../assets/release-platform-icons/SpotifyLogo.png"
//@ts-ignore
import AppleMusicLogo from "../../../assets/release-platform-icons/Apple-Music.png"
import SearchUserComponent from "../../ui/components/SearchUserComponent"

enum TabOptions {
  SPOTIFY = "Spotify",
  APPLE = "Apple"
}

const Playlists = ({
  paginatedTrackPlaylists,
  fetchTrackPlaylists,
  isAdmin
}: {
  paginatedTrackPlaylists: PaginatedData<TrackPlaylist>
  fetchTrackPlaylists: (
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined
  ) => Promise<void>
  isAdmin: boolean
}) => {
  const [loading, setLoading] = useState(true)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [inputTimeoutId, setInputTimeoutId] = useState(undefined)
  const [additionalFilters, setAdditionalFilters] = useState<{ [key: string]: string | undefined }>({})
  const [searchValue, setSearchValue] = useState<string>("")
  const searchOptions: KSelectOption[] = [
    { label: lang.playlists.playlist, value: 0, value2: "playlist" },
    { label: lang.playlists.track_title, value: 1, value2: "track_title" },
    { label: "ISRC", value: 2, value2: "isrc" },
    { label: lang.playlists.release, value: 3, value2: "release_name" },
    { label: lang.playlists.artist, value: 4, value2: "artist_name" }
  ]

  const [hidePastPlaylists, setHidePastPlaylists] = useState<boolean>(false)
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(searchOptions[0])

  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()
  const navigate = useNavigate()
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedTab, setSelectedTab] = useState(TabOptions.SPOTIFY)

  const [language, setLanguage] = useState<string>(() => getSelectedLanguage())

  useEffect(() => {
    let userId = ""
    if (isAdmin && selectedUser) {
      userId = `${selectedUser.value}`
    } else if (isAdmin && !selectedUser) {
      userId = "0"
    }

    clearTimeout(inputTimeoutId)
    setInputTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (selectedSearchOption?.value2) {
          setAdditionalFilters({
            playlist: selectedSearchOption?.value2 === "playlist" ? searchValue : "",
            track_title: selectedSearchOption?.value2 === "track_title" ? searchValue : "",
            isrc: selectedSearchOption?.value2 === "isrc" ? searchValue : "",
            release_name: selectedSearchOption?.value2 === "release_name" ? searchValue : "",
            artist_name: selectedSearchOption?.value2 === "artist_name" ? searchValue : "",
            hide_past: hidePastPlaylists ? "1" : "",
            user_id: userId,
            platform: selectedTab,
            is_admin_page: isAdmin ? "1" : ""
          })
          setPageNumber(1)
        }
      }, 500)
    )
  }, [selectedSearchOption, searchValue, hidePastPlaylists, selectedUser, selectedTab])

  const limit = 10

  const tableTitle = (
    <div className="flex flex-row gap-2 items-center !cursor-default self-start">
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        height="20px"
        arrowColor="#000"
        position="bottom"
        marginLeft={!openSidebar && language === "tr" ? "30px" : "0px"}
        content={
          <div className="h-5 w-max flex items-center">
            <KSpan
              text={openSidebar ? lang.playlists.hide_sidebar : lang.playlists.show_sidebar}
              fontSize={12}
              lineHeight="16px"
              color="#FFF"
            />
          </div>
        }
      >
        <div className="flex items-center">
          <KButton
            icon="/left-sidebar.svg"
            width="32px"
            height="32px"
            background="transparent"
            shadowDisabled={true}
            padding="4px"
            onClick={() => {
              if (setOpenSidebar) {
                setOpenSidebar(!openSidebar)
              }
              setLoading(true)
              setTimeout(() => {
                setLoading(false)
              }, 250)
            }}
          />
        </div>
      </KTooltip>
      <div>
        <KTitleSpan text={lang.playlists.title} color="#111" fontSize={isAdmin ? 20 : 28} lineHeight="28px" />
      </div>
    </div>
  )

  const onClickPage = async (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    setLoading(true)
    await fetchTrackPlaylists(limit, (pageNumber - 1) * limit, filters)
    setLoading(false)
    setPageNumber(pageNumber)
  }

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    // runs in first render (mount) without applying any filter or sort param
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(
        async () => {
          setLoading(true)
          await fetchTrackPlaylists(limit, 0, filters)
          setLoading(false)
          setPageNumber(1)
        },
        debounce ? 500 : 0
      )
    )
  }

  const topRightElements = (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <KSpan text={`${lang.playlists.hide_past_playlists}:`} color="#000" fontWeight={500} />
        <input
          type="checkbox"
          value=""
          checked={hidePastPlaylists}
          disabled={loading}
          style={{
            accentColor: "#000"
          }}
          onChange={() => {
            setHidePastPlaylists(!hidePastPlaylists)
          }}
        />
      </div>
      <img src="/vector.svg" alt="vector-icon" />
      <div
        className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px] w-[300px]"
        style={{
          background: searchValue ? "#FFF" : "#F7F7F7",
          border: "1px solid #F3F3F3",
          height: "48px",
          transition: "0.3s"
        }}
      >
        <KInput
          dataTestId="playlist-search"
          value={searchValue}
          padding="0px"
          background="transparent"
          onChange={(text: string) => {
            setSearchValue(text)
          }}
          leftIcon="/catalog_icons/search-grey.svg"
          gap="8px"
          borderRadius={0}
          shadowDisabled
          placeholder={"Search..."}
        />
        <KDropdown
          dataTestId="playlist-search-option"
          rightIcon="/admin_icons/caret-down.svg"
          options={searchOptions}
          selected={selectedSearchOption}
          onSelect={(selected) => {
            setSelectedSearchOption(selected as KSelectOption)
          }}
          padding="0px"
          background="transparent"
          activeBackground="transparent"
          shadowDisabled={true}
          enableIndicator={true}
          menuWidth={140}
          width={180}
          menuLeftMargin={-40}
          textColor="#F3F3F3"
        />
      </div>
    </div>
  )

  const topFilters = (
    <SearchUserComponent className="px-0 mb-6" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
  )

  return (
    <div className="catalog flex-grow justify-center">
      <div className="w-[100%] flex ">
        <div
          className={`${
            openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
          } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
          style={{
            transitionDuration: "0.5s"
          }}
        >
          <div
            className="pr-3 py-4 whitespace-nowrap flex gap-2"
            style={{
              paddingLeft: isAdmin ? 24 : 16
            }}
          >
            {!isAdmin && (
              <div
                style={{ textAlign: "left", display: "inline-block", cursor: "pointer" }}
                onClick={() => navigate(-1)}
              >
                <KLogo hoverEnabled={true} width={36} height={36} logoColor={"white"} borderRadius={1} />
              </div>
            )}
            <KTitleSpan text={lang.playlists.title} lineHeight="28px" fontSize={20} color="#111" />
          </div>

          <div>
            <div className="py-1.5 px-4">
              <div
                data-testid="left-bar-spotify"
                className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                  selectedTab === TabOptions.SPOTIFY ? "bg-[#FFF]" : "transparent"
                } cursor-pointer whitespace-nowrap`}
                onClick={() => {
                  if (selectedTab === TabOptions.SPOTIFY) return
                  setSelectedTab(TabOptions.SPOTIFY)
                }}
              >
                <img src={SpotifyLogo} className="h-6 w-auto" />
              </div>
              <div
                data-testid="left-bar-apple"
                className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                  selectedTab === TabOptions.APPLE ? "bg-[#FFF]" : "transparent"
                } cursor-pointer whitespace-nowrap`}
                onClick={() => {
                  if (selectedTab === TabOptions.APPLE) return
                  setSelectedTab(TabOptions.APPLE)
                }}
              >
                <img src={AppleMusicLogo} className="h-6 w-auto" />
                <KSpan text={"Apple Music"} fontSize={14} lineHeight="20px" fontWeight={500} color="#111" />
              </div>
            </div>
          </div>
        </div>
        <div className="grow min-h-0">
          <TableNew
            // className="!overflow-hidden"
            title={tableTitle}
            pagination
            minRowCount={limit}
            limit={limit}
            columns={playlistsColumns}
            data={paginatedTrackPlaylists.results}
            count={paginatedTrackPlaylists.count}
            loading={loading}
            topFilters={isAdmin ? topFilters : undefined}
            onClickPage={onClickPage}
            topRightElement={topRightElements}
            onSortOrFilterChange={onSortOrFilterChange}
            pageNumber={pageNumber}
            overrideTableOverflow="auto"
            additionalFilters={additionalFilters}
            notFoundTitle={lang.not_found.playlists.title}
            notFoundDesc={lang.not_found.playlists.desc}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    paginatedTrackPlaylists: state.insights.paginatedTrackPlaylists
  }
}

export default connect(mapStateToProps, { fetchTrackPlaylists })(Playlists)
